/**
 * ImportListPage — Issue #176.
 *
 * Lists HR roster import batches with status, row counts, and links to the
 * detail / preview screen.
 */
import { useEffect, useState } from 'react';
import { Link } from 'wouter';
import AccessDenied from '@/components/AccessDenied';
import { Card } from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
import { hasPermission, HR_PERMISSIONS } from '@/lib/hrPermissions';
import {
  HR_IMPORT_STATUS_LABELS,
  HR_IMPORT_STATUS_LIST,
  useHrImports,
  type HrEmployeeImportStatus,
} from '@/hooks/useHr';

const STATUS_TONE: Record<HrEmployeeImportStatus, string> = {
  parsing: 'bg-slate-100 text-slate-700',
  pending_review: 'bg-amber-100 text-amber-800',
  approved: 'bg-blue-100 text-blue-700',
  committed: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
  canceled: 'bg-slate-200 text-slate-600',
};

const BADGE_BASE = 'inline-flex rounded-full px-2 py-0.5 text-xs font-medium';

function fmt(value?: string | null): string {
  return value ? new Date(value).toLocaleString('zh-CN') : '—';
}

export default function ImportListPage() {
  const { permissions } = useAuth();
  const canRead = hasPermission(permissions, HR_PERMISSIONS.IMPORT_READ);
  const canWrite = hasPermission(permissions, HR_PERMISSIONS.IMPORT_WRITE);

  const { items, total, loading, error, fetchImports } = useHrImports();
  const [statusFilter, setStatusFilter] = useState<HrEmployeeImportStatus | ''>('');
  const [page, setPage] = useState(1);
  const limit = 20;

  useEffect(() => {
    if (!canRead) return;
    void fetchImports({
      status: statusFilter || undefined,
      page,
      limit,
    });
  }, [canRead, statusFilter, page, fetchImports]);

  if (!canRead) return <AccessDenied resource="员工导入批次" />;

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="space-y-4 p-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">员工花名册导入</h1>
          <p className="text-sm text-slate-500">
            上传 → 字段映射 → 数据校验 → 预览错误 → 审批确认 → 正式导入。
            所有状态变更均通过后端状态机校验并写入审计事件。
          </p>
        </div>
        {canWrite ? (
          <Link
            href="/hr/imports/create"
            className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary/90"
          >
            新建导入批次
          </Link>
        ) : null}
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm text-slate-600">
          状态
          <select
            className="rounded border border-slate-300 px-2 py-1 text-sm"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(
                e.target.value === ''
                  ? ''
                  : (e.target.value as HrEmployeeImportStatus),
              );
              setPage(1);
            }}
          >
            <option value="">全部</option>
            {HR_IMPORT_STATUS_LIST.map((s) => (
              <option key={s} value={s}>
                {HR_IMPORT_STATUS_LABELS[s]}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error ? (
        <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error.message}
        </div>
      ) : null}

      <Card className="overflow-hidden">
        {loading && items.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">加载中…</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            暂无导入批次。点击右上角“新建导入批次”开始上传。
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/40">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">批次 ID</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">源文件</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">状态</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">总行 / 合法 / 错误 / 重复</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">已新增 / 已更新 / 已跳过</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">创建时间</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">提交时间</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">操作</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr key={row.id} className="border-b last:border-0 hover:bg-muted/20">
                  <td className="px-4 py-3 font-mono text-xs">{row.id.slice(0, 8)}…</td>
                  <td className="px-4 py-3 text-muted-foreground">{row.sourceFilename}</td>
                  <td className="px-4 py-3">
                    <span className={cn(BADGE_BASE, STATUS_TONE[row.status])}>
                      {HR_IMPORT_STATUS_LABELS[row.status]}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {row.totalRows} / {row.validRows} / {row.errorRows} / {row.duplicateRows}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {row.insertedCount} / {row.updatedCount} / {row.skippedCount}
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{fmt(row.createdAt)}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{fmt(row.committedAt)}</td>
                  <td className="px-4 py-3">
                    <Link href={`/hr/imports/${row.id}`} className="text-primary hover:underline">
                      查看 / 处理
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {totalPages > 1 ? (
        <div className="flex items-center justify-end gap-2 text-sm">
          <button
            type="button"
            className="rounded-md border px-3 py-1.5 disabled:opacity-40"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            上一页
          </button>
          <span className="text-muted-foreground">
            {page} / {totalPages}（共 {total} 条）
          </span>
          <button
            type="button"
            className="rounded-md border px-3 py-1.5 disabled:opacity-40"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            下一页
          </button>
        </div>
      ) : null}
    </div>
  );
}
